import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, Download, FileText, Lock, MessageCircle, Play, Plus, Printer, Trash2, Unlock, Upload, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { api, dt, errMsg, exportCsv, rp, waLink } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-secondary/50" {...props} />
  </div>
);

const StatusBadge = ({ s }) => {
  const map = {
    aktif: "border-emerald-500/40 text-emerald-400",
    jatuh_tempo: "border-amber-500/40 text-amber-400",
    peringatan: "border-orange-500/40 text-orange-400",
    terlambat: "border-red-500/40 text-red-400",
    isolir: "border-red-500/40 text-red-400",
  };
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] ${map[s] || "border-border text-muted-foreground"}`}>{s}</span>;
};

const EMPTY = { name: "", phone: "", secret_name: "", profile: "", price: 0, due_day: 20, address: "", notes: "", customer_code: "" };

export default function FtthCustomers() {
  const [tab, setTab] = useState("list");
  const [rows, setRows] = useState([]);
  const [secrets, setSecrets] = useState([]);
  const [packages, setPackages] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const [bills, setBills] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [grace, setGrace] = useState({ cid: null, name: "", days: 3, note: "" });
  const [receipts, setReceipts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [bucket, setBucket] = useState("semua");
  const [q, setQ] = useState("");
  const [csv, setCsv] = useState({ open: false, file: null, update: false, skipSecret: false, result: null, preview: null, previewing: false });

  const thisMonth = new Date().toISOString().slice(0, 7);
  const isPast = period < thisMonth;
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    return d.toISOString().slice(0, 7);
  });

  const load = async () => {
    try {
      const [c, s, p, r, l, n, rc] = await Promise.all([
        api.get(`/ftth/customers?period=${period}`), api.get("/ppp/secrets"), api.get("/ftth/packages"),
        api.get("/ftth/reminders"), api.get("/ftth/isolir-logs"), api.get("/ftth/notifications"),
        api.get("/ftth/receipts"),
      ]);
      setRows(c.data); setSecrets(s.data); setPackages(p.data); setReminders(r.data); setLogs(l.data); setNotifs(n.data); setReceipts(rc.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period]);

  const counts = {
    semua: rows.length,
    terlambat: rows.filter((c) => c.bucket === "terlambat").length,
    jatuh_tempo: rows.filter((c) => c.bucket === "jatuh_tempo").length,
    lunas: rows.filter((c) => c.paid).length,
  };
  const shown = (() => {
    const base = bucket === "semua" ? rows : bucket === "lunas" ? rows.filter((c) => c.paid) : rows.filter((c) => c.bucket === bucket);
    const term = q.trim().toLowerCase();
    if (!term) return base;
    return base.filter((c) =>
      ["name", "customer_code", "phone", "secret_name", "profile", "address", "notes"].some((k) =>
        String(c[k] ?? "").toLowerCase().includes(term)
      ) || String(c.due_day ?? "").includes(term)
    );
  })();

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/ftth/customers", { ...form, price: Number(form.price), due_day: Number(form.due_day) });
      toast.success(`Pelanggan ${form.name} ditambahkan`); setForm(EMPTY); setTab("list"); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/ftth/customers/${edit.id}`, {
        name: edit.name, phone: edit.phone, secret_name: edit.secret_name, profile: edit.profile,
        price: Number(edit.price), due_day: Number(edit.due_day), address: edit.address || "", notes: edit.notes || "",
        customer_code: (edit.customer_code || "").trim(),
      });
      toast.success("Data pelanggan disimpan"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const act = async (c, path, msg) => {
    try { await api.post(`/ftth/customers/${c.id}/${path}`, {}); toast.success(msg); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (c) => {
    try { await api.delete(`/ftth/customers/${c.id}`); toast.success("Pelanggan dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const pay = async (c) => {
    try {
      const { data } = await api.post(`/ftth/customers/${c.id}/pay`, {});
      toast.success(`Pembayaran ${c.name} dicatat (${data.invoice})`);
      await load();
      const rc = await api.get(`/ftth/receipts?customer_id=${c.id}`);
      setReceipt(rc.data[0]);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openBills = async (c) => {
    try { const { data } = await api.get(`/ftth/customers/${c.id}/bills`); setBills(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveGrace = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ftth/customers/${grace.cid}/grace`, { days: Number(grace.days), note: grace.note });
      toast.success(`Isolir ditunda s/d ${data.grace_until}`);
      setGrace({ cid: null, name: "", days: 3, note: "" }); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const cancelGrace = async (c) => {
    try { await api.delete(`/ftth/customers/${c.id}/grace`); toast.success("Penundaan dibatalkan"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const importCustomers = async () => {
    const day = window.prompt("Impor semua secret PPPoE menjadi pelanggan. Tanggal jatuh tempo (1-31)?", "20");
    if (!day) return;
    try {
      const { data } = await api.post("/ftth/customers/import", { due_day: Number(day) });
      toast.success(`${data.imported} pelanggan diimpor dari router`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const previewCsv = async (file, opts = {}) => {
    if (!file) return;
    setCsv((c) => ({ ...c, previewing: true, preview: null, result: null }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("update_existing", (opts.update ?? csv.update) ? "true" : "false");
      fd.append("skip_secret_check", (opts.skipSecret ?? csv.skipSecret) ? "true" : "false");
      fd.append("dry_run", "true");
      const { data } = await api.post("/ftth/customers/import-csv", fd, { timeout: 300000 });
      setCsv((c) => ({ ...c, preview: data }));
    } catch (e) { toast.error(errMsg(e)); } finally { setCsv((c) => ({ ...c, previewing: false })); }
  };

  const importCsv = async () => {
    if (!csv.file) return toast.error("Pilih file CSV dulu");
    setBusy(true);
    setCsv((c) => ({ ...c, result: null }));
    try {
      const fd = new FormData();
      fd.append("file", csv.file);
      fd.append("update_existing", csv.update ? "true" : "false");
      fd.append("skip_secret_check", csv.skipSecret ? "true" : "false");
      const { data } = await api.post("/ftth/customers/import-csv", fd, { timeout: 300000 });
      setCsv((c) => ({ ...c, result: data }));
      toast.success(`Impor selesai: ${data.created} baru, ${data.updated} diperbarui`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/ftth/customers/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template-pelanggan-ftth.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runCheck = async () => {
    if (!window.confirm("Jalankan pemeriksaan jatuh tempo? Pelanggan yang lewat 2 hari dari jatuh tempo akan DIISOLIR otomatis.")) return;
    try { await api.post("/ftth/run-due-check", {}); toast.success("Pemeriksaan jatuh tempo dijalankan"); setTimeout(load, 4000); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Tabs value={tab} onValueChange={setTab} className="fade-up space-y-6">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
        {[["list", "Pelanggan"], ["add", "Add Pelanggan"], ["tagihan", "Tagihan & WhatsApp"], ["kuitansi", "Kuitansi"], ["isolir", "Riwayat Isolir"], ["notif", "Notifikasi"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-lg px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-violet-600/20">{l}</TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="list">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Cari nama / ID pelanggan / WA / secret / alamat…" value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="customer-search" className="h-9 w-[280px] rounded-full bg-secondary/50 text-xs"
            />
            {q && <Button size="sm" variant="ghost" className="rounded-full text-[11px]" onClick={() => setQ("")} data-testid="customer-search-clear">Bersihkan</Button>}
            <select
              className="h-9 rounded-full border border-input bg-secondary/50 px-3 text-xs font-mono" data-testid="period-picker"
              value={period} onChange={(e) => setPeriod(e.target.value)}
            >
              {months.map((m) => <option key={m} value={m}>{m}{m === thisMonth ? " (bulan ini)" : ""}</option>)}
            </select>
            {[["semua", "Semua"], ["terlambat", "Terlambat"], ["jatuh_tempo", "Jatuh Tempo"], ["lunas", "Lunas"]].map(([k, l]) => (
              <Button
                key={k} size="sm" variant={bucket === k ? "default" : "outline"} className="rounded-full text-[11px]"
                data-testid={`bucket-${k}`} onClick={() => setBucket(k)}
              >
                {l} ({counts[k]})
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white" onClick={importCustomers} data-testid="import-customers"><Download className="mr-1 h-3.5 w-3.5" /> Impor dari Router</Button>
            <Button size="sm" className="rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white" onClick={() => setCsv({ open: true, file: null, update: false, skipSecret: false, result: null })} data-testid="open-import-csv"><Upload className="mr-1 h-3.5 w-3.5" /> Impor CSV</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={runCheck} data-testid="run-due-check"><Play className="mr-1 h-3.5 w-3.5" /> Cek Jatuh Tempo</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsv(`pelanggan-ftth-${period}.csv`, shown)} data-testid="export-customers">Export CSV</Button>
          </div>
        </div>
        <p className="mb-3 text-sm text-muted-foreground" data-testid="period-info">
          Periode <b className="font-mono text-foreground">{period}</b> · {shown.length} pelanggan ditampilkan · jatuh tempo H-2 sudah masuk daftar tagih · isolir otomatis 2 hari setelah jatuh tempo
          {isPast ? " · mode riwayat (aksi dinonaktifkan)" : ""}
        </p>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="customers-table">
            <TableHeader><TableRow>
              <TableHead>ID Pelanggan</TableHead><TableHead>Nama</TableHead><TableHead>WA</TableHead><TableHead>Secret PPPoE</TableHead><TableHead>Paket</TableHead>
              <TableHead>Tagihan</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead>Lunas</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.id} className="text-xs">
                  <TableCell data-label="ID Pelanggan" className="font-mono text-cyan-400" data-testid={`cust-code-${c.name}`}>{c.customer_code || "-"}</TableCell>
                  <TableCell data-label="Nama" className="font-medium"><Link to={`/pelanggan/${c.id}`} className="hover:text-violet-400 transition-colors duration-200" data-testid={`open-card-${c.name}`}>{c.name}</Link></TableCell>
                  <TableCell data-label="WA" className="font-mono">{c.phone || "-"}</TableCell>
                  <TableCell data-label="Secret PPPoE" className="font-mono">{c.secret_name}</TableCell>
                  <TableCell data-label="Paket" className="font-mono">{c.profile || "-"}</TableCell>
                  <TableCell data-label="Tagihan" className="font-mono">{rp(c.price)}</TableCell>
                  <TableCell data-label="Jatuh Tempo" className="font-mono">
                    tgl {c.due_day}
                    {!c.paid && c.due_soon ? <span className="ml-1 rounded-full border border-amber-500/40 px-1.5 py-0.5 text-[10px] text-amber-400" data-testid={`due-soon-${c.name}`}>H-{Math.abs(c.days_past)}</span> : null}
                  </TableCell>
                  <TableCell data-label="Lunas" className="font-mono">{c.last_paid_period || "-"}</TableCell>
                  <TableCell data-label="Status">
                    {c.paid ? (
                      <span className="rounded-full border border-emerald-500/40 px-2 py-0.5 text-[10px] text-emerald-400">lunas {c.period}</span>
                    ) : (
                      <StatusBadge s={c.bucket === "terlambat" ? "terlambat" : c.bucket === "jatuh_tempo" ? "jatuh_tempo" : c.status} />
                    )}
                    {c.grace_until ? <span className="ml-1 rounded-full border border-cyan-500/40 px-2 py-0.5 text-[10px] text-cyan-400">tunda s/d {c.grace_until}</span> : null}
                  </TableCell>
                  <TableCell data-label="" className="space-x-1 text-right cell-actions">
                    {c.paid ? (
                      <Button size="sm" disabled className="rounded-full bg-emerald-600/30 text-emerald-300" data-testid={`paid-${c.name}`}>
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Lunas
                      </Button>
                    ) : (
                      <Button size="sm" className="rounded-full bg-emerald-600 text-white" onClick={() => pay(c)} data-testid={`pay-${c.name}`}><Wallet className="mr-1 h-3.5 w-3.5" /> Bayar</Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openBills(c)} data-testid={`bills-${c.name}`}><FileText className="h-3.5 w-3.5" /></Button>
                    {!isPast && (c.grace_until ? (
                      <Button size="sm" variant="outline" className="rounded-full text-cyan-400" onClick={() => cancelGrace(c)} data-testid={`cancel-grace-${c.name}`}><Clock className="h-3.5 w-3.5" /></Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setGrace({ cid: c.id, name: c.name, days: 3, note: "" })} data-testid={`grace-${c.name}`}><Clock className="h-3.5 w-3.5" /></Button>
                    ))}
                    {!isPast && (c.status === "isolir" ? (
                      <Button size="sm" variant="outline" className="rounded-full text-emerald-400" onClick={() => act(c, "restore", `${c.name} dibuka`)} data-testid={`restore-${c.name}`}><Unlock className="h-3.5 w-3.5" /></Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full text-amber-400" onClick={() => act(c, "isolir", `${c.name} diisolir`)} data-testid={`isolir-${c.name}`}><Lock className="h-3.5 w-3.5" /></Button>
                    ))}
                    {!isPast && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit({ ...c })} data-testid={`edit-${c.name}`}>Edit</Button>}
                    {!isPast && <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(c)} data-testid={`delete-${c.name}`}><Trash2 className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
              {!shown.length && <TableRow><TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground cell-empty">{loading ? "Memuat data…" : "Tidak ada pelanggan pada tab ini"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="add">
        <form onSubmit={create} className="max-w-2xl space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="customer-form">
          <h3 className="font-display text-lg font-semibold">Tambah Pelanggan FTTH</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nama Pelanggan" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" />
            <Field label="ID Pelanggan (kosongkan = otomatis)" value={form.customer_code} onChange={(e) => setForm({ ...form, customer_code: e.target.value })} data-testid="cust-code" />
            <Field label="No. WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" />
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Secret PPPoE</Label>
              <select
                required className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="cust-secret"
                value={form.secret_name}
                onChange={(e) => {
                  const s = secrets.find((x) => x.name === e.target.value);
                  setForm({ ...form, secret_name: e.target.value, profile: s?.profile || form.profile });
                }}
              >
                <option value="">— pilih secret —</option>
                {secrets.map((s) => <option key={s.id} value={s.name}>{s.name} ({s.profile})</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Paket FTTH</Label>
              <select
                className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="cust-package"
                value={form.profile}
                onChange={(e) => {
                  const p = packages.find((x) => x.name === e.target.value);
                  setForm({ ...form, profile: e.target.value, price: p?.price || form.price });
                }}
              >
                <option value="">— pilih paket —</option>
                {packages.map((p) => <option key={p.name} value={p.name}>{p.name} {p.rate_limit ? `(${p.rate_limit})` : ""} {p.price ? `— ${rp(p.price)}` : ""}</option>)}
              </select>
            </div>
            <Field label="Tagihan / Bulan" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="cust-price" />
            <Field label="Tanggal Jatuh Tempo (1-31)" type="number" min="1" max="31" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} data-testid="cust-due" />
            <Field label="Alamat" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cust-address" />
            <Field label="Catatan" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cust-notes" />
          </div>
          <Button type="submit" disabled={busy} data-testid="cust-submit" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
            <Plus className="mr-1 h-4 w-4" /> Simpan Pelanggan
          </Button>
        </form>
      </TabsContent>

      <TabsContent value="tagihan">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="reminders-table">
            <TableHeader><TableRow><TableHead>Pelanggan</TableHead><TableHead>Tagihan</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Pengingat</TableHead></TableRow></TableHeader>
            <TableBody>
              {reminders.map((r) => (
                <TableRow key={r.id} className="text-xs">
                  <TableCell data-label="Pelanggan" className="font-medium">{r.name}<span className="ml-2 font-mono text-muted-foreground">{r.phone}</span></TableCell>
                  <TableCell data-label="Tagihan" className="font-mono">{rp(r.price)}</TableCell>
                  <TableCell data-label="Jatuh Tempo" className="font-mono">tgl {r.due_day} {r.days_past > 0 ? `(+${r.days_past} hari)` : r.due_soon ? `(H-${Math.abs(r.days_past)})` : ""}</TableCell>
                  <TableCell data-label="Status">{r.paid ? <span className="text-emerald-400">lunas</span> : <StatusBadge s={r.status} />}</TableCell>
                  <TableCell data-label="Pengingat" className="text-right">
                    <Button asChild size="sm" disabled={!r.phone} className="rounded-full bg-emerald-600 text-white">
                      <a href={waLink(r.phone, r.message)} target="_blank" rel="noreferrer" data-testid={`wa-${r.name}`}>
                        <MessageCircle className="mr-1 h-3.5 w-3.5" /> Kirim WA
                      </a>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!reminders.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground cell-empty">{loading ? "Memuat…" : "Belum ada pelanggan"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="kuitansi">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="receipts-table">
            <TableHeader><TableRow><TableHead>No. Kuitansi</TableHead><TableHead>Pelanggan</TableHead><TableHead>Periode</TableHead><TableHead>Nominal</TableHead><TableHead>Waktu</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {receipts.map((r) => (
                <TableRow key={r.id} className="text-xs">
                  <TableCell data-label="No. Kuitansi" className="font-mono">{r.invoice}</TableCell>
                  <TableCell data-label="Pelanggan">{r.customer}</TableCell>
                  <TableCell data-label="Periode" className="font-mono">{r.period}</TableCell>
                  <TableCell data-label="Nominal" className="font-mono">{rp(r.amount)}</TableCell>
                  <TableCell data-label="Waktu" className="font-mono">{dt(r.created_at)}</TableCell>
                  <TableCell data-label="" className="space-x-1 text-right cell-actions">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setReceipt(r)} data-testid={`receipt-${r.invoice}`}><Printer className="mr-1 h-3.5 w-3.5" /> Kuitansi</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!receipts.length && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada pembayaran tercatat</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="isolir">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="isolir-logs-table">
            <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Pelanggan</TableHead><TableHead>Aksi</TableHead><TableHead>Oleh</TableHead><TableHead>Alasan</TableHead><TableHead>Profil</TableHead></TableRow></TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} className="font-mono text-xs">
                  <TableCell data-label="Waktu">{dt(l.created_at)}</TableCell>
                  <TableCell data-label="Pelanggan">{l.name} <span className="text-muted-foreground">({l.secret_name})</span></TableCell>
                  <TableCell data-label="Aksi">{l.action === "isolir" ? <span className="text-red-400">isolir</span> : <span className="text-emerald-400">buka</span>}</TableCell>
                  <TableCell data-label="Oleh">{l.by}</TableCell>
                  <TableCell data-label="Alasan" className="max-w-[260px] truncate">{l.reason}</TableCell>
                  <TableCell data-label="Profil">{l.prev_profile || "-"}</TableCell>
                </TableRow>
              ))}
              {!logs.length && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada riwayat isolir</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="notif">
        <div className="space-y-2" data-testid="notif-list">
          {notifs.map((n) => (
            <div key={n.id} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-xs">
              <div>
                <p className="font-medium">{n.name} <StatusBadge s={n.type} /></p>
                <p className="text-muted-foreground">{n.message}</p>
                <p className="font-mono text-[10px] text-muted-foreground">{dt(n.created_at)}</p>
              </div>
              {n.phone ? (
                <Button asChild size="sm" variant="outline" className="rounded-full">
                  <a href={waLink(n.phone, n.message)} target="_blank" rel="noreferrer" data-testid={`notif-wa-${n.id}`}><MessageCircle className="mr-1 h-3.5 w-3.5" /> WA</a>
                </Button>
              ) : null}
            </div>
          ))}
          {!notifs.length && <p className="text-sm text-muted-foreground">Belum ada notifikasi. Jalankan "Cek Jatuh Tempo" untuk membuat peringatan.</p>}
        </div>
      </TabsContent>

      {/* Dialog tunda isolir */}
      <Dialog open={!!grace.cid} onOpenChange={(o) => !o && setGrace({ cid: null, name: "", days: 3, note: "" })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Tunda Isolir — {grace.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Pelanggan tidak akan diisolir otomatis sampai tanggal tenggat baru. Jika sedang diisolir, layanan langsung dibuka.</p>
            <Field label="Perpanjang (hari, 1-30)" type="number" min="1" max="30" value={grace.days} onChange={(e) => setGrace({ ...grace, days: e.target.value })} data-testid="grace-days" />
            <Field label="Catatan" value={grace.note} onChange={(e) => setGrace({ ...grace, note: e.target.value })} data-testid="grace-note" />
            <Button onClick={saveGrace} disabled={busy} data-testid="grace-save" className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">Simpan Penundaan</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog riwayat tagihan */}
      <Dialog open={!!bills} onOpenChange={(o) => !o && setBills(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Riwayat Tagihan — {bills?.customer?.name}</DialogTitle></DialogHeader>
          {bills && (
            <div className="space-y-3">
              <div className="rounded-xl border border-border p-3 text-xs">
                <p>Belum lunas: <span className="font-mono text-amber-400">{bills.unpaid_periods.length} bulan</span></p>
                <p>Total tunggakan: <span className="font-mono text-red-400">{rp(bills.outstanding)}</span></p>
              </div>
              <div className="max-h-[320px] space-y-2 overflow-y-auto scrollbar-thin" data-testid="bills-list">
                {bills.bills.map((b) => (
                  <div key={b.period} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-xs">
                    <div>
                      <p className="font-mono">{b.period} · {rp(b.amount)}</p>
                      {b.paid ? <p className="font-mono text-[10px] text-muted-foreground">{b.invoice} · {dt(b.paid_at)}</p> : null}
                    </div>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${b.paid ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>
                      {b.paid ? "lunas" : "belum lunas"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog kuitansi */}
      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Kuitansi Pembayaran</DialogTitle></DialogHeader>
          {receipt && (
            <div className="space-y-4">
              <div id="print-area" className="rounded-xl border border-border p-5" data-testid="receipt-card">
                <p className="text-center text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{receipt.wifi_name || "ALPHANET"}</p>
                <p className="mt-1 text-center font-display text-lg font-semibold">KUITANSI PEMBAYARAN</p>
                <div className="mt-4 space-y-1 font-mono text-xs">
                  <p className="flex justify-between"><span className="text-muted-foreground">No. Kuitansi</span><span>{receipt.invoice}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Pelanggan</span><span>{receipt.customer}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Periode</span><span>{receipt.period}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Metode</span><span>{receipt.method || "tunai"}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Waktu</span><span>{dt(receipt.created_at)}</span></p>
                  <p className="mt-2 flex justify-between border-t border-dashed border-border pt-2 text-sm"><span>TOTAL</span><span className="font-semibold">{rp(receipt.amount)}</span></p>
                </div>
                <p className="mt-4 text-center text-[10px] text-emerald-400">LUNAS — terima kasih atas pembayaran Anda</p>
                {receipt.cs_wa ? <p className="mt-1 text-center font-mono text-[9px] text-muted-foreground">CS/WA: {receipt.cs_wa}</p> : null}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => window.print()} data-testid="receipt-print" className="flex-1 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white"><Printer className="mr-1 h-4 w-4" /> Cetak</Button>
                <Button asChild variant="outline" className="flex-1 rounded-full">
                  <a href={waLink(rows.find((r) => r.name === receipt.customer)?.phone || "", receipt.message)} target="_blank" rel="noreferrer" data-testid="receipt-wa">
                    <MessageCircle className="mr-1 h-4 w-4" /> Kirim WA
                  </a>
                </Button>
              </div>
              <Button variant="ghost" onClick={() => setReceipt(null)} data-testid="receipt-close" className="w-full rounded-full text-muted-foreground">Tutup</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Edit {edit?.name}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <Field label="Nama" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-cust-name" />
              <Field label="ID Pelanggan" value={edit.customer_code || ""} onChange={(e) => setEdit({ ...edit, customer_code: e.target.value })} data-testid="edit-cust-code" />
              <Field label="No. WhatsApp" value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} data-testid="edit-cust-phone" />
              <Field label="Tagihan" type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} data-testid="edit-cust-price" />
              <Field label="Jatuh Tempo (1-31)" type="number" min="1" max="31" value={edit.due_day} onChange={(e) => setEdit({ ...edit, due_day: e.target.value })} data-testid="edit-cust-due" />
              <Button onClick={save} disabled={busy} data-testid="edit-cust-save" className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">Simpan</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Impor CSV */}
      <Dialog open={csv.open} onOpenChange={(o) => setCsv((c) => ({ ...c, open: o }))}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="import-csv-dialog">
          <DialogHeader><DialogTitle className="font-display">Impor Pelanggan dari CSV</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-secondary/30 p-3 text-[11px] leading-relaxed text-muted-foreground">
              Kolom yang dikenali: <b className="text-foreground">nama, no_wa, secret_pppoe, paket, harga, jatuh_tempo, alamat, catatan, id_pelanggan</b>.
              Hanya <b className="text-foreground">nama</b> dan <b className="text-foreground">secret_pppoe</b> yang wajib. Baris dicocokkan berdasarkan secret PPPoE.
              <Button size="sm" variant="outline" className="mt-2 h-7 rounded-full text-[11px]" onClick={downloadTemplate} data-testid="download-template">
                <FileText className="mr-1 h-3 w-3" /> Unduh template CSV
              </Button>
            </div>
            <input
              type="file" accept=".csv,text/csv" data-testid="csv-file"
              onChange={(e) => { const f = e.target.files?.[0] || null; setCsv((c) => ({ ...c, file: f, result: null, preview: null })); previewCsv(f); }}
              className="block w-full cursor-pointer rounded-xl border border-border bg-secondary/50 p-2.5 text-xs file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-white"
            />
            <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-xs">
              <span>Perbarui data bila secret sudah terdaftar
                <span className="block text-[11px] text-muted-foreground">Jika tidak dicentang, baris yang sudah ada akan dilewati</span>
              </span>
              <input type="checkbox" checked={csv.update} onChange={(e) => { setCsv((c) => ({ ...c, update: e.target.checked })); previewCsv(csv.file, { update: e.target.checked }); }} data-testid="csv-update" className="h-4 w-4" />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-xs">
              <span>Abaikan pengecekan secret di router
                <span className="block text-[11px] text-muted-foreground">Pakai bila secret PPPoE belum dibuat di Mikrotik</span>
              </span>
              <input type="checkbox" checked={csv.skipSecret} onChange={(e) => { setCsv((c) => ({ ...c, skipSecret: e.target.checked })); previewCsv(csv.file, { skipSecret: e.target.checked }); }} data-testid="csv-skip-secret" className="h-4 w-4" />
            </label>
            {csv.previewing && <p className="text-[11px] text-muted-foreground">Membaca pratinjau…</p>}
            {csv.preview && !csv.result && (
              <div className="space-y-2" data-testid="csv-preview">
                <p className="text-xs font-medium">
                  Pratinjau: {csv.preview.total_rows} baris ·{" "}
                  <span className="text-emerald-500">{csv.preview.created} baru</span> ·{" "}
                  <span className="text-cyan-500">{csv.preview.updated} diperbarui</span> ·{" "}
                  <span className="text-muted-foreground">{csv.preview.skipped} dilewati</span>
                </p>
                <div className="max-h-52 overflow-auto rounded-xl border border-border">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-secondary/80">
                      <tr className="text-left">
                        <th className="px-2 py-1">Brs</th><th className="px-2 py-1">Nama</th><th className="px-2 py-1">Secret</th>
                        <th className="px-2 py-1">Paket</th><th className="px-2 py-1">Tagihan</th><th className="px-2 py-1">Tgl</th><th className="px-2 py-1">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(csv.preview.preview || []).map((r) => (
                        <tr key={r.row} className="border-t border-border/60">
                          <td className="px-2 py-1 font-mono">{r.row}</td>
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1 font-mono">{r.secret_name}</td>
                          <td className="px-2 py-1">{r.profile || "-"}</td>
                          <td className="px-2 py-1 font-mono">{rp(r.price)}</td>
                          <td className="px-2 py-1 font-mono">{r.due_day}</td>
                          <td className="px-2 py-1">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] ${r.action === "baru" ? "bg-emerald-500/15 text-emerald-500" : "bg-cyan-500/15 text-cyan-500"}`}>{r.action}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!!(csv.preview.errors || []).length && (
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-amber-500/30 bg-amber-500/5 p-2">
                    {csv.preview.errors.map((er, i) => (
                      <p key={i} className="text-[11px] text-amber-600 dark:text-amber-300">Baris {er.row} · {er.name}: {er.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button onClick={importCsv} disabled={busy || !csv.file} data-testid="csv-submit" className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white">
              <Upload className="mr-1 h-4 w-4" /> {csv.preview && !csv.result ? "Simpan Impor Sekarang" : "Mulai Impor"}
            </Button>
            {csv.result && (
              <div className="space-y-2" data-testid="csv-result">
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[["Baris", csv.result.total_rows], ["Baru", csv.result.created], ["Update", csv.result.updated], ["Lewat", csv.result.skipped]].map(([l, v]) => (
                    <div key={l} className="rounded-xl border border-border p-2">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{l}</p>
                      <p className="font-mono text-base font-semibold">{v}</p>
                    </div>
                  ))}
                </div>
                {!!(csv.result.errors || []).length && (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-amber-500/30 bg-amber-500/5 p-2">
                    {csv.result.errors.map((er, i) => (
                      <p key={i} className="text-[11px] text-amber-600 dark:text-amber-300">
                        Baris {er.row} · {er.name}: {er.reason}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
